import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { get, post, del } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import KpiCard from '../components/widgets/KpiCard';
import { fmtBRL, fmtBRLCompact } from '../lib/format';
import {
  ArrowDownToLine, ArrowUpFromLine, TrendingUp, Percent, Activity, Pencil, Trash2, ChevronLeft, ChevronRight,
} from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MONTH_FULL = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const TooltipDark = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-md p-3 shadow-xl text-[12px]">
      <div className="text-zinc-400 mb-1.5">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-zinc-300">{p.name}:</span>
          <span className="font-mono text-zinc-50">{fmtBRL(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function RadarAnual() {
  const { companies, companyFilter, competence } = useApp();
  const [year, setYear] = useState(parseInt(competence.split('-')[0], 10) || new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const cid = companyFilter === 'all' ? null : companyFilter;
      const params = { year };
      if (cid) params.company_id = cid;
      const r = await get('/radar/yearly', params);
      setData(r);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar Radar Anual');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [year, companyFilter]);

  const openEdit = (row) => {
    setEditForm({
      month: row.month,
      entrada: row.entrada ?? 0,
      saida: row.saida ?? 0,
    });
    setEditOpen(true);
  };

  const saveManual = async () => {
    if (editForm.entrada < 0 || editForm.saida < 0) return toast.error('Valores não podem ser negativos');
    try {
      const cid = companyFilter === 'all' ? null : companyFilter;
      await post('/radar/manual', {
        year,
        month: editForm.month,
        company_id: cid,
        entrada: Number(editForm.entrada) || 0,
        saida: Number(editForm.saida) || 0,
      });
      toast.success(`${MONTH_FULL[editForm.month - 1]} atualizado`);
      setEditOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const removeOverride = async (month) => {
    if (!window.confirm(`Remover override manual de ${MONTH_FULL[month - 1]}? Volta ao valor calculado das Entries.`)) return;
    try {
      const cid = companyFilter === 'all' ? null : companyFilter;
      const params = new URLSearchParams({ year, month });
      if (cid) params.set('company_id', cid);
      await del(`/radar/manual?${params.toString()}`);
      toast.success(`Override removido`);
      await load();
    } catch (e) {
      toast.error('Erro ao remover');
    }
  };

  const rows = data?.rows || [];
  const totals = data?.totals || { entrada: 0, saida: 0, lucro: 0, margem: 0 };

  // Dados para o gráfico (todos os meses)
  const chartData = rows.map((r) => ({
    mes: MONTH_LABELS[r.month - 1],
    Entrada: r.entrada,
    Saída: r.saida,
    'Lucro Acumulado': r.lucro_acumulado,
  }));

  return (
    <div className="fade-in">
      <TopBar
        title="Radar Anual"
        subtitle={`Histórico mensal Jan→Dez de ${year} · Entradas, Saídas, Lucro Líquido e Margem`}
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setYear(year - 1)}
              className="border-zinc-700 text-zinc-300"
              data-testid="radar-year-prev"
            >
              <ChevronLeft size={14} />
            </Button>
            <div className="font-display font-semibold text-lg text-zinc-100 min-w-[60px] text-center" data-testid="radar-year-label">
              {year}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setYear(year + 1)}
              className="border-zinc-700 text-zinc-300"
              data-testid="radar-year-next"
            >
              <ChevronRight size={14} />
            </Button>
          </div>
        }
      />

      <div className="px-8 py-7 space-y-5">
        {/* KPIs totais do ano */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KpiCard label={`Entradas ${year}`} value={totals.entrada} accent="#10B981" icon={ArrowDownToLine} testid="radar-kpi-entrada" />
          <KpiCard label={`Saídas ${year}`} value={totals.saida} accent="#EF4444" icon={ArrowUpFromLine} testid="radar-kpi-saida" />
          <KpiCard label={`Lucro Líquido ${year}`} value={totals.lucro} accent={totals.lucro >= 0 ? '#10B981' : '#EF4444'} icon={TrendingUp} testid="radar-kpi-lucro" />
          <KpiCard label={`Margem Anual`} value={totals.margem} format="percent" accent="#D4AF37" icon={Percent} testid="radar-kpi-margem" highlight />
        </div>

        {/* TABELA */}
        <SectionCard
          title={`Tabela Mensal — ${year}`}
          subtitle="Clique no ícone de lápis para editar manualmente um mês (sobrescreve cálculo)"
          padded={false}
        >
          <div className="overflow-x-auto">
            <table className="w-full ceap-table" data-testid="radar-table">
              <thead>
                <tr>
                  <th>Mês</th>
                  <th className="text-right">Entrada</th>
                  <th className="text-right">Saída</th>
                  <th className="text-right">Lucro</th>
                  <th className="text-right">Margem</th>
                  <th className="text-right">Lucro Acum.</th>
                  <th className="text-right">Margem Acum.</th>
                  <th className="text-center">Origem</th>
                  <th className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9} className="text-center text-zinc-500 py-6">Carregando...</td></tr>
                )}
                {!loading && rows.map((r) => (
                  <tr key={r.competence} data-testid={`radar-row-${r.month}`}>
                    <td className="font-mono text-zinc-100">
                      <strong>{MONTH_LABELS[r.month - 1]}</strong>
                      <span className="text-zinc-500 ml-1">/{year}</span>
                    </td>
                    <td className="text-right font-mono emerald-text">{fmtBRL(r.entrada)}</td>
                    <td className="text-right font-mono red-text">{fmtBRL(r.saida)}</td>
                    <td className={`text-right font-mono font-semibold ${r.lucro >= 0 ? 'emerald-text' : 'red-text'}`}>
                      {fmtBRL(r.lucro)}
                    </td>
                    <td className={`text-right font-mono ${r.lucro >= 0 ? 'emerald-text' : 'red-text'}`}>
                      {r.margem.toFixed(1)}%
                    </td>
                    <td className={`text-right font-mono ${r.lucro_acumulado >= 0 ? 'gold-text' : 'red-text'}`}>
                      {fmtBRL(r.lucro_acumulado)}
                    </td>
                    <td className={`text-right font-mono ${r.lucro_acumulado >= 0 ? 'gold-text' : 'red-text'}`}>
                      {r.margem_acumulada.toFixed(1)}%
                    </td>
                    <td className="text-center">
                      {r.is_manual ? (
                        <span className="pill pill-gold" data-testid={`radar-manual-flag-${r.month}`}>Manual</span>
                      ) : (
                        <span className="pill pill-muted">Auto</span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openEdit(r)}
                          className="p-1.5 rounded hover:bg-blue-900/30 text-zinc-400 hover:text-blue-400"
                          title="Editar manualmente"
                          data-testid={`radar-edit-${r.month}`}
                        >
                          <Pencil size={12} />
                        </button>
                        {r.is_manual && (
                          <button
                            onClick={() => removeOverride(r.month)}
                            className="p-1.5 rounded hover:bg-red-900/30 text-zinc-400 hover:text-red-400"
                            title="Remover override manual"
                            data-testid={`radar-remove-${r.month}`}
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && rows.length > 0 && (
                  <tr className="font-bold bg-zinc-900/50 border-t-2 border-zinc-700">
                    <td className="text-zinc-100">TOTAL {year}</td>
                    <td className="text-right font-mono emerald-text">{fmtBRL(totals.entrada)}</td>
                    <td className="text-right font-mono red-text">{fmtBRL(totals.saida)}</td>
                    <td className={`text-right font-mono ${totals.lucro >= 0 ? 'emerald-text' : 'red-text'}`}>
                      {fmtBRL(totals.lucro)}
                    </td>
                    <td className={`text-right font-mono ${totals.lucro >= 0 ? 'emerald-text' : 'red-text'}`}>
                      {totals.margem.toFixed(1)}%
                    </td>
                    <td colSpan={4}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </SectionCard>

        {/* GRÁFICO */}
        <SectionCard
          title={`Histórico Visual — ${year}`}
          subtitle="Barras: Entradas/Saídas | Linha dourada: Lucro Acumulado"
        >
          {rows.length === 0 || rows.every((r) => r.entrada === 0 && r.saida === 0) ? (
            <div className="py-10 text-center text-zinc-500 text-sm">
              Sem dados para {year} — adicione lançamentos manuais clicando no lápis na tabela acima
            </div>
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="mes" stroke="#52525B" fontSize={11} />
                  <YAxis yAxisId="left" stroke="#52525B" fontSize={10} tickFormatter={fmtBRLCompact} />
                  <YAxis yAxisId="right" orientation="right" stroke="#D4AF37" fontSize={10} tickFormatter={fmtBRLCompact} />
                  <Tooltip content={<TooltipDark />} />
                  <Legend wrapperStyle={{ fontSize: 11, color: '#A1A1AA' }} />
                  <Bar yAxisId="left" dataKey="Entrada" fill="#10B981" radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="left" dataKey="Saída" fill="#EF4444" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="Lucro Acumulado" stroke="#D4AF37" strokeWidth={2.5} dot={{ r: 4, fill: '#D4AF37' }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </SectionCard>
      </div>

      {/* DIALOG de edição manual */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-display text-zinc-50">
              Lançamento Manual — {editForm && MONTH_FULL[editForm.month - 1]} / {year}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Os valores manuais SUBSTITUEM o cálculo automático das Entries naquele mês.
              Os valores aqui NÃO afetam o DRE/Dashboard — ficam apenas no Radar Anual.
            </DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <Label className="text-zinc-300">Entrada (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.entrada}
                  onChange={(e) => setEditForm({ ...editForm, entrada: e.target.value })}
                  className="bg-zinc-900 border-zinc-700"
                  data-testid="radar-input-entrada"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Saída (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.saida}
                  onChange={(e) => setEditForm({ ...editForm, saida: e.target.value })}
                  className="bg-zinc-900 border-zinc-700"
                  data-testid="radar-input-saida"
                />
              </div>
              <div className="col-span-2 pt-2 text-xs text-zinc-500">
                Lucro calculado: <strong className="emerald-text font-mono">
                  {fmtBRL((Number(editForm.entrada) || 0) - (Number(editForm.saida) || 0))}
                </strong>
                {(Number(editForm.entrada) || 0) > 0 && (
                  <span className="ml-3">
                    Margem: <strong className="gold-text">
                      {(((Number(editForm.entrada) || 0) - (Number(editForm.saida) || 0)) / Number(editForm.entrada) * 100).toFixed(1)}%
                    </strong>
                  </span>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="border-zinc-700 text-zinc-300" data-testid="radar-cancel">
              Cancelar
            </Button>
            <Button onClick={saveManual} className="btn-gold" data-testid="radar-save">
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
